import { NodeProps } from "reactflow";
import { SingleResponseNode } from "../../../types/bert";
import { Box, Popover } from "@mui/material";

import HandlerWrapper from "../Handlers/HandlerWrapper";
import CreateSingleResponseBlockType from "./CreateSingleResponseBlockType";
import EditLegendSvg from "../../svg/EditLegendSvg";
import DeleteLegendSvg from "../../svg/DeleteLegendSvg";
import NotOperatorLegendSvg from "../../svg/NotOperatorLegendSvg";
import { useReactFlowStateContextProvider } from "../../hooks/useReactFlowStateContextProvider";
import { SingleResponseBlockType } from "../../context/NodeStateContext";
import BlockNodeUI from "./BlockNodeUI";
import useAnchorEl from "@callveriq/hooks/useAnchorEl";
import AttributeBlock from "../Blocks/AttributeBlock";
import BertCardWrapper from "../BertCardWrapper";
import ParameterBlock from "../Blocks/ParameterBlock";
import MomentsBlock from "../Blocks/MomentsBlock";
import PredefinedFuncBlock from "../Blocks/PredefinedFuncBlock";
import NodeStatePorvider from "../../context/NodeStatePorvider";
import InfoLegendSvg from "../../svg/InfoLegendSvg";
import DialogButton from "@callveriq/components/custom_components/Button/DialogButton";
import ValidateInfoBlock from "../Blocks/ValidateInfoBlock";
import { singleNodeTypesMap } from "../../constants";
import ViewBlockPopOver from "../Blocks/ViewBlocks/ViewBlockPopOver";
import useParameterAiConfigContext from "../../hooks/useParameterAiConfigContext";
import { isDefined } from "@callveriq/utils/helper/common.helper";

const SingleBlockNode: React.FC<
    NodeProps<SingleResponseNode<NonNullable<SingleResponseBlockType>>>
> = (props) => {
    const { currentVersion } = useParameterAiConfigContext();
    const { isGroupBlockTree, deleteNode, setNodes } =
        useReactFlowStateContextProvider();
    const {
        open: editOpen,
        anchorEl: editAnchor,
        handleClick: handleEditClick,
        handleClose: handleEditClose,
    } = useAnchorEl<HTMLButtonElement | null>();
    const {
        open: infoOpen,
        handleClick: handleInfoClick,
        handleClose: handleInfoClose,
        anchorEl: infoAnchor,
    } = useAnchorEl<HTMLButtonElement | null>();
    const {
        id,
        data: { type, is_not, metadata },
    } = props;

    const { name } = metadata;

    return (
        <HandlerWrapper AddComponent={CreateSingleResponseBlockType} {...props}>
            <BlockNodeUI
                {...{
                    name,
                    heading: singleNodeTypesMap[type],
                    is_not,
                }}
            >
                <Box className="flex items-center" gap={0.5}>
                    <button onClick={handleInfoClick}>
                        <InfoLegendSvg />
                    </button>
                    {!isDefined(currentVersion) && (
                        <>
                            <button onClick={handleEditClick}>
                                <EditLegendSvg />
                            </button>
                            <DialogButton
                                onDelete={() => {
                                    return new Promise(() => {
                                        deleteNode(id);
                                    });
                                }}
                                title="Delete Node"
                                message="Are you sure you want to delete this Node?"
                                DeleteOptionComponent={
                                    <button>
                                        <DeleteLegendSvg />
                                    </button>
                                }
                            />

                            <button
                                onClick={() => {
                                    setNodes((nodes) =>
                                        nodes.map((e) =>
                                            e.id === id
                                                ? {
                                                      ...e,
                                                      data: {
                                                          ...e.data,
                                                          is_not: !is_not,
                                                      },
                                                  }
                                                : e
                                        )
                                    );
                                }}
                            >
                                <NotOperatorLegendSvg />
                            </button>
                        </>
                    )}
                </Box>
            </BlockNodeUI>
            <Popover
                open={editOpen}
                anchorEl={editAnchor}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: isGroupBlockTree ? "bottom" : "top",
                    horizontal: "center",
                }}
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: "10px",
                    },
                }}
            >
                <NodeStatePorvider
                    onNodeSaveCallBack={() => handleEditClose()}
                    nodeIdToUpdate={id}
                    nodeDataToUpdate={props.data.metadata}
                >
                    <BertCardWrapper
                        className="w-[500px]"
                        onClick={(e) => {
                            const target = e.target as HTMLElement;
                            if (
                                target &&
                                target.classList.contains("block_close_button")
                            ) {
                                if (editOpen) handleEditClose();
                                else handleInfoClose();
                            }
                        }}
                    >
                        {type === "attribute" ? <AttributeBlock /> : <></>}
                        {type === "parameter" ? <ParameterBlock /> : <></>}
                        {type === "moment" ? <MomentsBlock /> : <></>}
                        {type === "function" ? <PredefinedFuncBlock /> : <></>}
                        {type === "validateInfo" ? (
                            <ValidateInfoBlock />
                        ) : (
                            <></>
                        )}
                    </BertCardWrapper>
                </NodeStatePorvider>
            </Popover>
            <ViewBlockPopOver
                open={infoOpen}
                anchorEl={infoAnchor}
                onClose={handleInfoClose}
                id={id}
                data={props.data.metadata}
                type={type}
            />
        </HandlerWrapper>
    );
};

export default SingleBlockNode;
